import {
  CheckmarkFilled,
  ErrorFilled,
  Time,
  WarningFilled,
} from '@carbon/icons-react';
import {
  GitHubWorkflowRun,
  GitHubWorkflowRunConclusion,
  GitHubWorkflowRunStatus,
} from '@gql/gql-types';
import { Chip } from '@mui/material';

const getWorkflowRunChipProps = (workflowRun?: GitHubWorkflowRun | null) => {
  // No workflow run yet
  if (!workflowRun) {
    return {
      label: 'Starting',
      color: 'primary' as const,
      icon: <Time size={16} />,
    };
  }

  const { status, conclusion } = workflowRun;

  // Pending states
  if (
    status === GitHubWorkflowRunStatus.Queued ||
    status === GitHubWorkflowRunStatus.InProgress
  ) {
    return {
      label: 'Pending',
      color: 'primary' as const,
      icon: <Time size={16} />,
    };
  }

  // Completed states
  if (status === GitHubWorkflowRunStatus.Completed) {
    switch (conclusion) {
      case GitHubWorkflowRunConclusion.Success:
        return {
          label: 'Success',
          color: 'success' as const,
          icon: <CheckmarkFilled size={16} />,
        };
      case GitHubWorkflowRunConclusion.Failure:
        return {
          label: 'Failed',
          color: 'error' as const,
          icon: <ErrorFilled size={16} />,
        };
      default:
        // cancelled, skipped, etc
        return {
          label: 'Error',
          color: 'warning' as const,
          icon: <WarningFilled size={16} />,
        };
    }
  }

  // Fallback
  return {
    label: status,
    color: 'default' as const,
    icon: <WarningFilled size={16} />,
  };
};

export const GithubWorkflowRun = ({
  workflowRun,
}: {
  workflowRun?: GitHubWorkflowRun | null;
}) => {
  const chipProps = getWorkflowRunChipProps(workflowRun);

  return (
    <Chip
      {...chipProps}
      variant='outlined'
      clickable
      component={workflowRun ? 'a' : 'div'}
      href={workflowRun?.url || ''}
      target='_blank'
      rel='noopener noreferrer'
      sx={{
        pl: 1,
        pr: 0.5,
        textDecoration: 'none',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    />
  );
};
